import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Badge, Card, Input, List, Row, Tooltip, TreeSelect } from 'antd';
import DealershipChooser from 'components/DealershipChooser';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster/dist/leaflet.markercluster-src.js';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen';
import _ from 'lodash';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { LocationService } from 'services';
import CarStore from 'stores/CarStore';
import ReportsDataStore from 'stores/ReportsDataStore';
import ShopStore from 'stores/ShopStore';
import styled from 'styled-components';
import { PitstopButton } from '../../shared/PitstopUI/PitstopButton';
import { PitstopSwitch } from '../../shared/PitstopUI/PitstopSwitch';
import {
  CriticalMarker,
  HealthyMarker,
  MajorMarker,
  MinorMarker,
  ShopMarker,
} from './CustomMarkers';

import { showDemoVinOrRealVin } from 'shared/utils';

import { AppStore, CurrentUserStore } from 'stores';

import {
  CloseOutlined,
  InfoOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Filter } from 'components';
import { queryUtils } from 'helpers';
import FoodExpressShopData from './foodExpressShopList.json';

const SidebarList = styled.div`
  height: 490px;
  overflow-x: auto;
  width: 300px;
  margin-left: 0px;

  .ant-list-pagination {
    text-align: -webkit-center;
  }
`;

const ButtonsContainer = styled.div`
  margin-bottom: 10px;
  position: relative;
  display: flex;
  flex-direction: column;

  button {
    margin-right: auto;
    margin-bottom: 8px;
  }
`;

const MapArea = styled.div`
  flex: 1;
  position: relative;
  height: 490px;
  z-index: 2;
  margin-left: 20px;

  &&& .leaflet-tooltip-right {
    margin-left: 18px;
    background: rgba(0, 0, 0, 0.9);
    color: #fff;
    border: none;
    border-radius: 0;
  }
  &&& .leaflet-tooltip-right:before {
    border-right-color: rgba(0, 0, 0, 0);
  }
`;

const LocationItem = styled(List.Item)`
  && {
    background: ${(props) => (props.selected ? '#f0f8ff' : 'inherit')};
    padding: 12px 8px;
    display: flex;
    align-items: center;
  }

  button {
    margin-top: 12px;
  }

  .flex {
    flex: 1;
  }

  a {
    margin-left: 12px;
  }

  .last-update-at {
    font-size: 14px;
  }

  .last-update-at span {
    font-weight: bold;
  }

  .vin {
    font-weight: bold;
  }
`;

const LegendArea = styled.div`
  z-index: 1000;
  position: absolute;
  top: 8px;
  right: 8px;
`;

const Legend = styled(Card)`
  && {
    position: relative;
    right: 40px;
    top: 0px;
    padding: 16px;
    z-index: 1;
    max-width: 240px;
  }
  && .ant-card-body {
    padding: 0px;
  }
  && .ant-badge {
    display: block;
  }
  && .ant-btn {
    position: absolute;
    right: 16px;
    top: 16px;
  }
`;
const InfoButton = styled(PitstopButton)`
  && {
    position: absolute;
    z-index: 2;
    right: 0;
  }
`;

const Container = styled.div`
  margin: 10px 0px;
  display: flex;
  flex-direction: column;

  #servicePriority {
    display: flex;

    @media only screen and (max-width: 768px) {
      flex-direction: column;

      p,
      div {
        width: 100%;
      }
    }

    @media only screen and (min-width: 768px) {
      p {
        width: 12em;
        text-align: right;
        margin-right: 1em;
      }

      #filter {
        width: 21em;
      }
    }
  }

  #filter {
    display: flex;
    margin-bottom: 1em;

    @media only screen and (max-width: 768px) {
      flex-direction: column;

      p,
      div {
        width: 100%;
      }
    }

    @media only screen and (min-width: 768px) {
      p {
        width: 12em;
        text-align: right;
        margin-right: 1em;
      }
    }
  }
`;

const ShopMarkerFilter = styled.div`
  display: flex;
  min-width: 300px;
  margin-bottom: 1em;

  @media only screen and (max-width: 768px) {
    flex-direction: column;

    p,
    div {
      width: 100%;
    }
  }

  @media only screen and (min-width: 768px) {
    p {
      width: 12em;
      text-align: right;
      margin-right: 1em;
    }
  }
`;

const FoodExpressTreeSelectData = FoodExpressShopData.map(({ Name, id }) => ({
  title: Name,
  value: id,
}));
const FoodExpressShopAllIds = FoodExpressShopData.map(({ id }) => id);

const LeafletMap = React.forwardRef(
  (
    {
      locations,
      selectedShops,
      getMarkerIcon,
      getShopMarkerIcon,
      onMarkerClick,
    },
    ref
  ) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const layerGroupRef = useRef(null);
    const markersRef = useRef({});

    useEffect(() => {
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
        shadowUrl:
          'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
      });
    }, []);

    useEffect(() => {
      if (mapContainerRef.current && !mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapContainerRef.current);

        const osmUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        const osmAttrib =
          'Map data © <a href="https://openstreetmap.org">OpenStreetMap</a> contributors';
        const osm = new L.TileLayer(osmUrl, { attribution: osmAttrib });
        mapInstanceRef.current.addLayer(osm);

        mapInstanceRef.current.addControl(new L.Control.Fullscreen());

        layerGroupRef.current = L.layerGroup().addTo(mapInstanceRef.current);

        mapInstanceRef.current.on('click', () => {
          onMarkerClick(null);
        });
      }

      return () => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      if (mapInstanceRef.current && layerGroupRef.current) {
        layerGroupRef.current.clearLayers();
        markersRef.current = {};

        const markers = [];

        if (locations && locations.length > 0) {
          locations.forEach((item) => {
            const { location, carName, vin, id } = item;
            if (location && location.latitude && location.longitude) {
              const marker = L.marker([location.latitude, location.longitude], {
                icon: getMarkerIcon(id),
              })
                .bindTooltip(carName || showDemoVinOrRealVin(vin), {
                  permanent: true,
                  direction: 'right',
                })
                .bindPopup(
                  `${carName || showDemoVinOrRealVin(vin)}<p>Last seen at ${moment(location.timestamp * 1000).fromNow()}</p>`
                )
                .on('click', () => onMarkerClick(id));

              marker.addTo(layerGroupRef.current);
              markers.push(marker);
              markersRef.current[id] = { marker, location };
            }
          });
        }

        if (selectedShops && selectedShops.length > 0) {
          selectedShops.forEach((shop) => {
            if (shop.Latitude && shop.Longitude) {
              const marker = L.marker([shop.Latitude, shop.Longitude], {
                icon: getShopMarkerIcon(),
              })
                .bindTooltip(shop.Name, {
                  permanent: true,
                  direction: 'right',
                })
                .bindPopup(
                  _.map(shop, (val, key) => `<p>${key}: ${val}</p>`).join('')
                );

              marker.addTo(layerGroupRef.current);
              markers.push(marker);
            }
          });
        }

        if (markers.length > 0) {
          const group = L.featureGroup(markers);
          mapInstanceRef.current.fitBounds(group.getBounds(), {
            padding: [50, 50],
          });
        }
      }
    }, [
      locations,
      selectedShops,
      getMarkerIcon,
      getShopMarkerIcon,
      onMarkerClick,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        setFocus: (location, zoomLevel) => {
          if (
            mapInstanceRef.current &&
            location?.latitude &&
            location?.longitude
          ) {
            mapInstanceRef.current.setView(
              [location.latitude, location.longitude],
              zoomLevel,
              {
                animate: true,
                pan: { duration: 0.3 },
              }
            );
          }
        },
        focusMarker: (id, zoomLevel = 16) => {
          const markerInfo = markersRef.current[id];
          if (mapInstanceRef.current && markerInfo) {
            mapInstanceRef.current.setView(
              [markerInfo.location.latitude, markerInfo.location.longitude],
              zoomLevel,
              {
                animate: true,
                pan: { duration: 0.3 },
              }
            );
            markerInfo.marker.openPopup();
          }
        },
        map: mapInstanceRef.current,
        layerGroup: layerGroupRef.current,
      }),
      [mapInstanceRef.current, layerGroupRef.current, markersRef.current]
    );

    return (
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
    );
  }
);

const LiveLocationPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedShopIds, setSelectedShopIds] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filterKeyValues, setFilterKeyValues] = useState({
    servicePriority: [],
    carName: '',
    vin: '',
  });

  const mapRef = useRef(null);

  useEffect(() => {
    const filteredLocations = _([...LocationService.locationReports])
      .filter(
        (el) => el.location.longitude !== null && el.location.latitude !== null
      )
      .value();

    setLocations(filteredLocations);
  }, [LocationService.locationReports, LocationService.loadedLocation]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (autoRefresh) {
        refresh();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    const shopDisposer = observe(ShopStore.currentShop, 'id', async () => {
      await refresh();
    });

    return () => shopDisposer();
  }, []);

  useEffect(() => {
    const demoDisposer = observe(CarStore, 'demo', async () => {
      if (_.isBoolean(CarStore.demo)) {
        CarStore.demo ? await refresh() : LocationService.reset();
      }
    });

    return () => demoDisposer();
  }, []);

  useEffect(() => {
    AppStore.setSelectedSidebarKey('/location');
    CarStore.demo !== false ? refresh() : LocationService.reset();
  }, []);

  useEffect(() => {
    if (selectedId && mapRef.current) {
      mapRef.current.focusMarker(selectedId, 16);
    }
  }, [selectedId]);

  const refresh = async () => {
    setFilterKeyValues({
      servicePriority: [],
      carName: '',
      vin: '',
    });

    await load();
  };

  const load = async (params = {}) => {
    if (ShopStore.currentShop.id !== -1) {
      LocationService.loadedLocation = false;
      ReportsDataStore.drivingReport = null;

      await ReportsDataStore.getDrivingReport(ShopStore.currentShop.id);
      await ReportsDataStore.getMaintenanceSummaryReport(
        ShopStore.currentShop.id
      );

      await LocationService.fetchLocationForShop(
        ShopStore.currentShop.id,
        params
      );

      // Update locations state after fetch
      const filteredLocations = _([...LocationService.locationReports])
        .filter(
          (el) =>
            el.location.longitude !== null && el.location.latitude !== null
        )
        .value();
      setLocations(filteredLocations);

      setCurrentPage(1);
      checkCarLocationParam();
    }
  };

  const checkCarLocationParam = () => {
    const defaultCarLocation = queryUtils.getSearchParamByKey(
      window.location.search,
      'car'
    );
    if (defaultCarLocation && firstLoad) {
      const carLocation = _.find(
        locations,
        (location) => location.id === Number(defaultCarLocation)
      );
      if (carLocation) {
        setFirstLoad(false);
        setFilterKeyValues((prev) => ({
          ...prev,
          carName: carLocation.carName,
        }));
        onFilter('carName', carLocation.carName);
        setSelectedId(Number(defaultCarLocation));
      }
    }
  };

  const getMarker = (carId) => {
    const priority = getServicePriority(carId);

    switch (priority) {
      case 'minor':
        return MinorMarker;
      case 'critical':
        return CriticalMarker;
      case 'major':
        return MajorMarker;
      default:
        return HealthyMarker;
    }
  };

  const getShopMarker = () => {
    return ShopMarker;
  };

  const getStatusColor = (carId) => {
    const priority = getServicePriority(carId);

    switch (priority) {
      case 'critical':
        return '#eb3d34';
      case 'major':
        return '#e28931';
      case 'minor':
        return '#e2eb34';
      default:
        return '#52c41a';
    }
  };

  const getServicePriority = (carId) => {
    const reportDetail = _.filter(
      ReportsDataStore.totalServiceCountPerCar,
      (report) => Number(report.carId) === Number(carId)
    )[0];

    if (!_.isNil(reportDetail)) {
      const {
        critical_service_count,
        major_service_count,
        minor_service_count,
      } = reportDetail;

      if (critical_service_count > 0) return 'critical';
      if (major_service_count > 0) return 'major';
      if (minor_service_count > 0) return 'minor';
    }

    return 'success';
  };

  const setSelectedShopValues = (ids) => {
    setSelectedShopIds(ids);
  };

  const debounceTimerRef = useRef(null);
  const onFilter = async (key, value) => {
    const newFilterValues = {
      ...filterKeyValues,
      [key]: value,
    };
    setFilterKeyValues(newFilterValues);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(async () => {
      await applyFilters(newFilterValues);
    }, 800);
  };

  const applyFilters = async (filterValues) => {
    let params = {};
    _.forEach(filterValues, (value, key) => {
      if (!_.isEmpty(value)) {
        if (key === 'servicePriority') {
          params['hasServicePriority'] = _.map(
            filterValues.servicePriority,
            (value) => value.toLowerCase()
          ).toString();
        } else {
          params[key] = value;
        }
      }
    });

    await load(params);

    if (mapRef.current && mapRef.current.layerGroup) {
      mapRef.current.layerGroup.clearLayers();
    }
  };

  const shouldShowShopMarker = () => {
    const foodExpressShopIds = [
      1241, 1245, 1246, 1247, 1249, 1250, 1251, 1253, 1252,
    ];

    return (
      CurrentUserStore?.user?.role === 'admin' ||
      (import.meta.env.VITE_ENV === 'production' &&
        foodExpressShopIds.includes(ShopStore.currentShop.id))
    );
  };

  const selectedShops = _.filter(FoodExpressShopData, (shopData) =>
    _.includes(selectedShopIds, shopData.id)
  );

  const loading =
    !LocationService.loadedLocation &&
    ShopStore.currentShop.id !== -1 &&
    CarStore.demo !== false;

  return (
    <Container>
      <div id="dealership">
        <h1>Live Location</h1>
        <DealershipChooser />
      </div>

      <div id="servicePriority">
        <p>Filter by Service Priority:</p>
        <div id="filter">
          <Filter
            defaultKeys={['Critical', 'Major', 'Minor']}
            placeholder="Select priority to view vehicle locations"
            filterValues={filterKeyValues.servicePriority}
            onChange={(keys) => onFilter('servicePriority', keys)}
          />
        </div>
      </div>

      <div id="filter">
        <p>Filter by VIN:</p>
        <div>
          <Tooltip trigger={['focus']} title="Vin" placement="topLeft">
            <Input
              placeholder="Vin"
              name="vin"
              value={filterKeyValues.vin}
              prefix={<SearchOutlined />}
              onChange={(e) => onFilter('vin', e.target.value)}
            />
          </Tooltip>
        </div>
      </div>

      <div id="filter">
        <p>Filter by Unit ID:</p>
        <div>
          <Tooltip trigger={['focus']} title="Unit ID" placement="topLeft">
            <Input
              placeholder="Unit ID"
              name="carName"
              value={filterKeyValues.carName}
              prefix={<SearchOutlined />}
              onChange={(e) => onFilter('carName', e.target.value)}
            />
          </Tooltip>
        </div>
      </div>

      {shouldShowShopMarker() && (
        <ShopMarkerFilter>
          <p>Outside shops on map:</p>
          <div>
            <TreeSelect
              allowClear={true}
              placeholder="Select a shop"
              treeCheckable={true}
              showCheckedStrategy={TreeSelect.SHOW_CHILD}
              style={{ width: '300px' }}
              dropdownStyle={{ maxHeight: '300px' }}
              onChange={(ids) => setSelectedShopValues(ids)}
              value={selectedShopIds}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+ ${omittedValues.length} Shops ...`
              }
              treeData={[
                {
                  title:
                    selectedShopIds.length === FoodExpressShopData.length ? (
                      <span
                        onClick={() => setSelectedShopValues([])}
                        style={{
                          display: 'inline-block',
                          color: '#286FBE',
                          cursor: 'pointer',
                        }}
                      >
                        Unselect all
                      </span>
                    ) : (
                      <span
                        onClick={() =>
                          setSelectedShopValues(FoodExpressShopAllIds)
                        }
                        style={{
                          display: 'inline-block',
                          color: '#286FBE',
                          cursor: 'pointer',
                        }}
                      >
                        Select all
                      </span>
                    ),
                  value: 'xxx',
                  disableCheckbox: true,
                  disabled: true,
                },
                ...FoodExpressTreeSelectData,
              ]}
            />
          </div>
        </ShopMarkerFilter>
      )}

      <Row>
        <Card
          style={{ width: '100%' }}
          bodyStyle={{ display: 'flex', margin: 10, padding: 0, width: '100%' }}
        >
          <SidebarList>
            <ButtonsContainer>
              <PitstopButton
                icon={<ReloadOutlined />}
                onClick={async () => await refresh()}
              >
                Refresh
              </PitstopButton>
              <PitstopSwitch
                checkedChildren="auto refresh"
                unCheckedChildren="auto refresh"
                defaultChecked={autoRefresh}
                onChange={(checked) => setAutoRefresh(checked)}
              />
            </ButtonsContainer>
            <List
              pagination={{
                current: currentPage,
                position: 'bottom',
                pageSize: 10,
                total: locations.length,
                simple: true,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
              }}
              dataSource={locations}
              loading={loading}
              renderItem={(item) => (
                <LocationItem selected={item.id === selectedId}>
                  <div className="flex">
                    <div>
                      <Badge
                        color={getStatusColor(item.id)}
                        text={
                          item.carName ||
                          `${item.make} ${item.model} ${item.year}`
                        }
                      />
                    </div>
                    <p className="last-update-at">
                      <span>Last Update At: </span>
                      {moment
                        .tz(
                          item?.location?.timestamp * 1000,
                          CurrentUserStore.user.settings.timezone
                        )
                        .fromNow()}{' '}
                      (
                      {moment
                        .tz(
                          item?.location?.timestamp * 1000,
                          CurrentUserStore.user.settings.timezone
                        )
                        .format('lll')}
                      )
                    </p>
                    <p className="vin">{showDemoVinOrRealVin(item.vin)}</p>
                    <div>
                      <PitstopButton
                        onClick={() => {
                          setSelectedId(item.id);
                        }}
                        type="primary"
                      >
                        View Vehicle Location
                      </PitstopButton>
                      <Link to={`car/${item.id}`}>
                        <PitstopButton type="secondary">
                          Vehicle Profile
                        </PitstopButton>
                      </Link>
                    </div>
                    <div />
                  </div>
                </LocationItem>
              )}
            />
          </SidebarList>

          <MapArea>
            <LeafletMap
              ref={mapRef}
              locations={locations}
              selectedShops={selectedShops}
              getMarkerIcon={getMarker}
              getShopMarkerIcon={getShopMarker}
              onMarkerClick={(id) => setSelectedId(id)}
            />

            <LegendArea>
              <InfoButton
                type="dashed"
                shape="circle"
                style={{
                  backgroundColor: showLegend ? '#fff' : '#4d81c3',
                  color: showLegend ? '#000' : '#fff',
                }}
                icon={showLegend ? <CloseOutlined /> : <InfoOutlined />}
                onClick={() => setShowLegend(!showLegend)}
              />
              {!showLegend ? null : (
                <Legend>
                  <p>Vehicle Health Legend</p>
                  <Badge color="#52c41a" text="No Service Due" />
                  <Badge color="#eb3d34" text="Critical Service Required" />
                  <Badge color="#e28931" text="Major Service Required" />
                  <Badge color="#e2eb34" text="Minor Service Required" />
                </Legend>
              )}
            </LegendArea>
          </MapArea>
        </Card>
      </Row>
    </Container>
  );
};

export default observer(LiveLocationPage);
